/**
 * The ONE technical-system taxonomy.
 *
 * A "system" is a technology WTC either operates for a client (engineer loop —
 * `companies.technical_identifiers`) or that the client uses and we can sell
 * against (sales loop — `companies.current_technologies`). Before this registry
 * the same taxonomy existed twice, unrelated: a 12-slug advisory hint list in
 * `apps/hub/lib/companies/technical-identifiers.ts` and a 71-entry gazetteer in
 * `@west-tennessee-consulting/ml`. The gazetteer's own comment said its slug "MUST match
 * technical_identifiers.system" — nothing enforced that, and the two lists had
 * already drifted (the hint list offered `google_cloud`/`okta`/`duo`/`sophos`,
 * which the gazetteer never had; the gazetteer knew 60 systems the identifier
 * editor never suggested).
 *
 * So the slug set lives HERE, `@west-tennessee-consulting/ml`'s gazetteer composes its entries from
 * this table, and drift becomes a type error instead of a silent mismatch.
 *
 * `fields` is the second half of the typing: a system declares the identifier
 * labels an engineer should actually record and the SHAPE each value takes, so
 * "Tenant ID" is one canonical label rather than five spellings, and a value
 * that cannot be a tenant id can be flagged at the point of entry.
 */

/** Category of system — drives grouping in pickers and the ML feature set. */
export const TECHNICAL_SYSTEM_KINDS = [
  "managed_platform",
  "saas",
  "endpoint",
  "network",
  "email",
  "web",
  "telephony",
] as const;

export type TechnicalSystemKind = (typeof TECHNICAL_SYSTEM_KINDS)[number];

/** Which loop cares about a system: engineer (identifiers), sales, or both. */
export type TechnicalSystemAudience = "engineer" | "sales" | "both";

/**
 * Value shapes we can check. Deliberately small and syntactic — this validates
 * FORM, never existence, and is advisory everywhere (see `value-formats.ts`).
 */
export const TECHNICAL_IDENTIFIER_FORMATS = [
  "text",
  "uuid",
  "domain",
  "email",
  "url",
  "digits",
  "hex32",
  "aws_account_id",
  "google_customer_id",
  "google_ads_customer_id",
  "gcp_project_id",
  "stripe_account_id",
] as const;

export type TechnicalIdentifierFormat = (typeof TECHNICAL_IDENTIFIER_FORMATS)[number];

/** One identifier a system canonically carries. */
export interface TechnicalIdentifierFieldDef {
  /** Canonical label, in its canonical casing (e.g. "Tenant ID"). */
  readonly label: string;
  /** Shape of the value, used for advisory validation + input hints. */
  readonly format: TechnicalIdentifierFormat;
  /** Where an engineer finds it. Shown as help text in the editor. */
  readonly description: string;
  /** Example value, used as the input placeholder. */
  readonly example?: string;
}

/** One technology in the taxonomy. */
export interface TechnicalSystemDef {
  /** Canonical slug — the value stored in `technical_identifiers[].system`. */
  readonly slug: string;
  /** Human display name. The ONLY place a system's label is written. */
  readonly display: string;
  readonly kind: TechnicalSystemKind;
  readonly audiences: readonly TechnicalSystemAudience[];
  /** Alternate slugs a human/model types, normalized back to `slug`. */
  readonly slugAliases?: readonly string[];
  /** Canonical identifier fields. Absent = freeform labels only. */
  readonly fields?: readonly TechnicalIdentifierFieldDef[];
}

/**
 * The registry. Sorted by slug; add a system here (never in a consumer) and it
 * becomes available to the CRM editor, the Meridian tools, the Slack cards, and
 * the ML gazetteer at once.
 */
const TECHNICAL_SYSTEMS_LITERAL = [
  {
    slug: "adobe",
    display: "Adobe",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "apple_business_manager",
    display: "Apple Business Manager",
    kind: "managed_platform",
    audiences: ["engineer"],
    slugAliases: ["abm"],
    fields: [
      { label: "Organization ID", format: "text", description: "Apple Business Manager organization id" },
      { label: "Managed Apple ID", format: "email", description: "Administrator Managed Apple ID", example: "admin@contoso.com" },
    ],
  },
  {
    slug: "apple_endpoints",
    display: "Apple / Mac",
    kind: "endpoint",
    audiences: ["engineer"],
  },
  {
    slug: "att",
    display: "AT&T",
    kind: "telephony",
    audiences: ["sales"],
  },
  {
    slug: "aws",
    display: "Amazon Web Services",
    kind: "managed_platform",
    audiences: ["both"],
    slugAliases: ["amazon_web_services", "amazon_aws"],
    fields: [
      { label: "Account ID", format: "aws_account_id", description: "12-digit AWS account number", example: "123456789012" },
      { label: "Sign-in URL", format: "url", description: "IAM account sign-in URL", example: "https://123456789012.signin.aws.amazon.com/console" },
    ],
  },
  {
    slug: "aws_lightsail",
    display: "AWS Lightsail",
    kind: "managed_platform",
    audiences: ["engineer"],
    slugAliases: ["lightsail"],
    fields: [
      { label: "Instance Name", format: "text", description: "Lightsail instance name" },
    ],
  },
  {
    slug: "aws_route_53",
    display: "AWS Route 53",
    kind: "network",
    audiences: ["engineer"],
    slugAliases: ["route53", "route_53"],
    fields: [
      { label: "Hosted Zone ID", format: "text", description: "Route 53 hosted zone id", example: "Z1D633PJN98FT9" },
    ],
  },
  {
    slug: "azure",
    display: "Microsoft Azure",
    kind: "managed_platform",
    audiences: ["both"],
    slugAliases: ["microsoft_azure", "azure_subscription"],
    fields: [
      { label: "Subscription ID", format: "uuid", description: "Azure subscription the client's resources live in", example: "3f2504e0-4f89-11d3-9a0c-0305e82c3301" },
      { label: "Tenant ID", format: "uuid", description: "Entra tenant that owns the subscription", example: "8f9a1b2c-3d4e-5f60-7a8b-9c0d1e2f3a4b" },
    ],
  },
  {
    slug: "calendly",
    display: "Calendly",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "canva",
    display: "Canva",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "checkr",
    display: "Checkr",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "cloudflare",
    display: "Cloudflare",
    kind: "network",
    audiences: ["engineer"],
    fields: [
      { label: "Account ID", format: "hex32", description: "Cloudflare account id (dashboard URL segment)", example: "023e105f4ecef8ad9ca31a8372d0c353" },
      { label: "Zone ID", format: "hex32", description: "Per-domain zone id", example: "023e105f4ecef8ad9ca31a8372d0c353" },
    ],
  },
  {
    slug: "cpanel",
    display: "cPanel",
    kind: "web",
    audiences: ["engineer"],
    fields: [
      { label: "Control Panel URL", format: "url", description: "cPanel sign-in URL", example: "https://server.host.com:2083" },
    ],
  },
  {
    slug: "datadog",
    display: "Datadog",
    kind: "managed_platform",
    audiences: ["engineer"],
    fields: [
      { label: "Org Name", format: "text", description: "Datadog organization name" },
    ],
  },
  {
    slug: "dell_endpoints",
    display: "Dell laptops",
    kind: "endpoint",
    audiences: ["engineer"],
  },
  {
    slug: "digitalspace",
    display: "DigitalSpace",
    kind: "email",
    audiences: ["engineer"],
  },
  {
    slug: "dropbox",
    display: "Dropbox",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "duo",
    display: "Duo Security",
    kind: "network",
    audiences: ["engineer"],
    fields: [
      { label: "API Hostname", format: "domain", description: "Duo Admin API hostname", example: "api-1234abcd.duosecurity.com" },
    ],
  },
  {
    slug: "elementor",
    display: "Elementor",
    kind: "web",
    audiences: ["engineer"],
  },
  {
    slug: "evoice",
    display: "eVoice",
    kind: "telephony",
    audiences: ["both"],
  },
  {
    slug: "fortinet",
    display: "Fortinet",
    kind: "network",
    audiences: ["engineer"],
    fields: [
      { label: "Serial Number", format: "text", description: "FortiGate serial as registered to FortiCloud", example: "FGT60F0000000000" },
    ],
  },
  {
    slug: "givebutter",
    display: "Givebutter",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "godaddy",
    display: "GoDaddy",
    kind: "web",
    audiences: ["sales"],
    fields: [
      { label: "Customer Number", format: "text", description: "GoDaddy customer (shopper) number" },
    ],
  },
  {
    slug: "google_ads",
    display: "Google Ads / Ad Grants",
    kind: "saas",
    audiences: ["sales"],
    slugAliases: ["adwords", "google_ad_grants"],
    fields: [
      { label: "Customer ID", format: "google_ads_customer_id", description: "Google Ads customer id", example: "123-456-7890" },
    ],
  },
  {
    slug: "google_cloud",
    display: "Google Cloud",
    kind: "managed_platform",
    audiences: ["engineer"],
    slugAliases: ["gcp", "google_cloud_platform"],
    fields: [
      { label: "Project ID", format: "gcp_project_id", description: "Human-readable GCP project id", example: "contoso-prod-1234" },
      { label: "Project Number", format: "digits", description: "Numeric GCP project number", example: "183812250887" },
    ],
  },
  {
    slug: "google_search_console",
    display: "Google Search Console",
    kind: "web",
    audiences: ["engineer"],
    fields: [
      { label: "Property URL", format: "url", description: "Verified Search Console property", example: "https://contoso.org/" },
    ],
  },
  {
    slug: "google_workspace",
    display: "Google Workspace",
    kind: "managed_platform",
    audiences: ["both"],
    slugAliases: ["gsuite", "g_suite", "google_apps"],
    fields: [
      { label: "Customer ID", format: "google_customer_id", description: "Admin console → Account settings → Customer ID", example: "C01ab2cd3" },
      { label: "Primary Domain", format: "domain", description: "Primary domain of the Workspace account", example: "contoso.org" },
    ],
  },
  {
    slug: "gravity_forms",
    display: "Gravity Forms",
    kind: "web",
    audiences: ["engineer"],
  },
  {
    slug: "hostgator",
    display: "HostGator",
    kind: "web",
    audiences: ["engineer"],
    fields: [
      { label: "Control Panel URL", format: "url", description: "HostGator cPanel sign-in URL" },
    ],
  },
  {
    slug: "hp_endpoints",
    display: "HP desktops",
    kind: "endpoint",
    audiences: ["engineer"],
  },
  {
    slug: "hubspot",
    display: "HubSpot",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "ipage",
    display: "iPage",
    kind: "web",
    audiences: ["engineer"],
    fields: [
      { label: "Control Panel URL", format: "url", description: "iPage control panel sign-in URL" },
    ],
  },
  {
    slug: "lenovo_endpoints",
    display: "Lenovo laptops",
    kind: "endpoint",
    audiences: ["engineer"],
  },
  {
    slug: "levelset",
    display: "Levelset",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "mailchimp",
    display: "Mailchimp",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "malwarebytes",
    display: "Malwarebytes",
    kind: "network",
    audiences: ["engineer"],
  },
  {
    slug: "meraki",
    display: "Cisco Meraki",
    kind: "network",
    audiences: ["engineer"],
    slugAliases: ["cisco_meraki"],
    fields: [
      { label: "Organization ID", format: "digits", description: "Meraki dashboard organization id", example: "549236" },
    ],
  },
  {
    slug: "meta_business_suite",
    display: "Meta Business Suite",
    kind: "saas",
    audiences: ["sales"],
    slugAliases: ["meta", "facebook_business"],
    fields: [
      { label: "Business ID", format: "digits", description: "Meta business manager id", example: "1234567890123456" },
    ],
  },
  {
    slug: "microsoft_365",
    display: "Microsoft 365",
    kind: "managed_platform",
    audiences: ["both"],
    slugAliases: ["m365", "ms365", "microsoft365", "office_365", "o365", "office365", "exchange_online", "entra", "azure_ad"],
    fields: [
      { label: "Tenant ID", format: "uuid", description: "Directory (tenant) ID from Entra ID → Overview", example: "8f9a1b2c-3d4e-5f60-7a8b-9c0d1e2f3a4b" },
      { label: "Primary Domain", format: "domain", description: "The tenant's primary onmicrosoft/vanity domain", example: "contoso.com" },
    ],
  },
  {
    slug: "microsoft_edge",
    display: "Microsoft Edge",
    kind: "web",
    audiences: ["engineer"],
  },
  {
    slug: "microsoft_planner",
    display: "Microsoft Planner",
    kind: "managed_platform",
    audiences: ["engineer"],
  },
  {
    slug: "microsoft_teams",
    display: "Microsoft Teams",
    kind: "managed_platform",
    audiences: ["engineer"],
    slugAliases: ["teams", "ms_teams"],
  },
  {
    slug: "monday_com",
    display: "monday.com",
    kind: "saas",
    audiences: ["both"],
    slugAliases: ["monday"],
  },
  {
    slug: "mysql",
    display: "MySQL",
    kind: "network",
    audiences: ["engineer"],
    fields: [
      { label: "Host", format: "text", description: "Database host the client's app connects to" },
    ],
  },
  {
    slug: "network_for_good",
    display: "Network for Good",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "ninja_forms",
    display: "Ninja Forms",
    kind: "web",
    audiences: ["engineer"],
  },
  {
    slug: "nordpass",
    display: "NordPass",
    kind: "saas",
    audiences: ["both"],
  },
  {
    slug: "okta",
    display: "Okta",
    kind: "managed_platform",
    audiences: ["engineer"],
    fields: [
      { label: "Org URL", format: "url", description: "Okta org sign-in URL", example: "https://contoso.okta.com" },
    ],
  },
  {
    slug: "onedrive",
    display: "OneDrive",
    kind: "managed_platform",
    audiences: ["engineer"],
  },
  {
    slug: "openvpn",
    display: "OpenVPN",
    kind: "network",
    audiences: ["engineer"],
  },
  {
    slug: "paypal",
    display: "PayPal",
    kind: "saas",
    audiences: ["both"],
  },
  {
    slug: "quickbooks",
    display: "QuickBooks",
    kind: "saas",
    audiences: ["sales"],
    slugAliases: ["qbo", "quickbooks_online"],
    fields: [
      { label: "Realm ID", format: "digits", description: "QuickBooks company (realm) id", example: "9341454835303232" },
    ],
  },
  {
    slug: "race_roster",
    display: "Race Roster",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "relias",
    display: "Relias (LMS)",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "ringcentral",
    display: "RingCentral",
    kind: "telephony",
    audiences: ["both"],
    fields: [
      { label: "Account ID", format: "text", description: "RingCentral account id" },
    ],
  },
  {
    slug: "sage_accounting",
    display: "Sage Accounting",
    kind: "saas",
    audiences: ["both"],
    slugAliases: ["sage"],
  },
  {
    slug: "salesforce",
    display: "Salesforce",
    kind: "saas",
    audiences: ["both"],
  },
  {
    slug: "scalefusion",
    display: "Scalefusion (MDM)",
    kind: "network",
    audiences: ["engineer"],
    fields: [
      { label: "Account ID", format: "text", description: "Scalefusion account/organization id" },
    ],
  },
  {
    slug: "sharepoint",
    display: "SharePoint",
    kind: "managed_platform",
    audiences: ["both"],
    fields: [
      { label: "Root Site URL", format: "url", description: "SharePoint root site URL", example: "https://contoso.sharepoint.com" },
    ],
  },
  {
    slug: "sonicwall",
    display: "SonicWall",
    kind: "network",
    audiences: ["engineer"],
    fields: [
      { label: "Serial Number", format: "text", description: "Appliance serial registered to MySonicWall", example: "18B169000000" },
    ],
  },
  {
    slug: "sophos",
    display: "Sophos",
    kind: "network",
    audiences: ["engineer"],
    fields: [
      { label: "Customer ID", format: "text", description: "Sophos Central customer id" },
    ],
  },
  {
    slug: "stampli",
    display: "Stampli",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "sterling_volunteers",
    display: "Sterling Volunteers",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "stripe",
    display: "Stripe",
    kind: "saas",
    audiences: ["sales"],
    fields: [
      { label: "Account ID", format: "stripe_account_id", description: "Stripe account id", example: "acct_1A2b3C4d5E6f7G8h" },
    ],
  },
  {
    slug: "techsoup",
    display: "TechSoup",
    kind: "saas",
    audiences: ["engineer"],
    fields: [
      { label: "Account Username", format: "text", description: "Client's TechSoup account username" },
    ],
  },
  {
    slug: "trend_micro",
    display: "Trend Micro",
    kind: "email",
    audiences: ["engineer"],
  },
  {
    slug: "ubiquiti",
    display: "Ubiquiti / UniFi",
    kind: "network",
    audiences: ["engineer"],
  },
  {
    slug: "verizon",
    display: "Verizon",
    kind: "telephony",
    audiences: ["sales"],
  },
  {
    slug: "waste_management",
    display: "Waste Management (hauler)",
    kind: "saas",
    audiences: ["sales"],
  },
  {
    slug: "windows_defender",
    display: "Windows Defender",
    kind: "network",
    audiences: ["engineer"],
    slugAliases: ["microsoft_defender", "defender"],
  },
  {
    slug: "wordfence",
    display: "Wordfence",
    kind: "web",
    audiences: ["engineer"],
  },
  {
    slug: "wordpress",
    display: "WordPress",
    kind: "web",
    audiences: ["both"],
    fields: [
      { label: "Admin URL", format: "url", description: "wp-admin sign-in URL", example: "https://contoso.org/wp-admin" },
    ],
  },
  {
    slug: "xcitius",
    display: "Xcitius",
    kind: "saas",
    audiences: ["both"],
  },
  {
    slug: "youtube",
    display: "YouTube",
    kind: "web",
    audiences: ["sales"],
  },
  {
    slug: "zoom",
    display: "Zoom",
    kind: "telephony",
    audiences: ["sales"],
  },
] as const satisfies readonly TechnicalSystemDef[];

/**
 * The registry, widened to the interface. `TECHNICAL_SYSTEMS_LITERAL` keeps the
 * `as const` literal types alive so the slug union can be derived from the data;
 * consumers read the widened view, where every optional property exists.
 */
export const TECHNICAL_SYSTEMS: readonly TechnicalSystemDef[] = TECHNICAL_SYSTEMS_LITERAL;

/** Slug union of every system in the registry. */
export type KnownTechnicalSystemSlug = (typeof TECHNICAL_SYSTEMS_LITERAL)[number]["slug"];

/**
 * A stored system slug. Known slugs autocomplete; freeform stays legal because
 * a support engineer must be able to record a system we have not catalogued yet
 * (that is the whole reason this column is jsonb and not a foreign key).
 */
export type TechnicalSystemSlug = KnownTechnicalSystemSlug | (string & {});

export const KNOWN_TECHNICAL_SYSTEM_SLUGS: readonly KnownTechnicalSystemSlug[] =
  TECHNICAL_SYSTEMS_LITERAL.map((s) => s.slug);
